<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="sl-related-articles">
        <article-grid-item
            v-for="article in articles"
            :key="article.id"
            :article="article"
            :filter-badges="getFilterBadgesCallback()(article)"
            class="sl-related-articles__item"
        />
    </div>
</template>


<!-- /////////////////////////////////////////////////////////////////////////// SCRIPT -->

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { sampleSize } from 'lodash-es';
import { useArticleGridComposable } from '@/composables/ArticleGridComposable';
import constants from '@/constants/constants';
import Arrays from '@/utils/Arrays';
import ArticleGridItem from '@/components/articles-grid/ArticleGridItem.vue';


// ------------------------------------------------------------ COMPOSABLES

const store = useStore();
const { getFilterBadgesCallback } = useArticleGridComposable();


// ------------------------------------------------------------ DATA

const articleCodes = ref([]);


// ------------------------------------------------------------ COMPUTED

const articles = computed(() => store.state.catalog.articles.filter(
    a => articleCodes.value.includes(a.code)
));

/**
 * Tags of lenses that are in the basket (need to be matched with cleaning
 * products or accessories).
 */
const basketLensTags = computed(() =>
{
    const compatibleTagCodes = new Map();
    const articleCodes = store.state.basket.lines.map(l => l.article_code);
    for(const articleCode of articleCodes)
    {
        const article = store.getters['catalog/getByCode'](articleCode);
        if(!article)
        {
            continue;
        }

        for(const tag of article.tags)
        {
            const code = tag.code;
            const compatibleCode = `COMPATIBLE_WITH_${code}`;
            if(compatibleTagCodes.has(compatibleCode))
            {
                continue;
            }

            // Check if the matching `COMPATIBLE_WITH_*` tag exists
            const label = store.getters['catalog/getTagLabelByCode'](compatibleCode);
            if(typeof label !== 'undefined')
            {
                compatibleTagCodes.set(compatibleCode, true);
            }
        }
    }

    return Array.from(compatibleTagCodes.keys());
});


// ------------------------------------------------------------ METHODS

function suggest()
{
    const selectedCleaningProducts = [];
    const selectedAccessories      = [];

    // Retrieve cleaning products
    const allCleaningProducts = store.state.catalog.articles
        .filter(a => a.tags.some(t => t.code === 'CLEANING_PRODUCT'))
        .filter(a => a.status === constants.articles.status.ACTIVE);

    // Retrieve accessories
    const allAccessories = store.state.catalog.articles
        .filter(a => a.tags.some(t => t.code === 'ACCESSORY'))
        .filter(a => a.status === constants.articles.status.ACTIVE);

    // Suffle arrays
    Arrays.shuffle(allCleaningProducts);
    Arrays.shuffle(allAccessories);

    // Pick 2 distinct random cleaning products that share at least
    // one other tag with something from the basket.
    selectedCleaningProducts.push(
        ...sampleSize(
            allCleaningProducts.filter(a => a.tags.some(t => basketLensTags.value.includes(t.code))),
            2
        )
    );

    // Pick 2 distinct random accessories that share at least
    // one other tag with something from the basket.
    selectedAccessories.push(
        ...sampleSize(
            allAccessories.filter(a => a.tags.some(t => basketLensTags.value.includes(t.code))),
            2
        )
    );

    // Ensure there are 2 cleaning products
    if(selectedCleaningProducts.length < 2)
    {
        selectedCleaningProducts.push(...sampleSize(
            allCleaningProducts.filter(a => !selectedCleaningProducts.includes(a.code)),
            2 - selectedCleaningProducts.length
        ));
    }

    // Ensure there are 2 accessories
    if(selectedAccessories.length < 2)
    {
        selectedAccessories.push(...sampleSize(
            allAccessories.filter(a => !selectedAccessories.includes(a.code)),
            2 - selectedAccessories.length
        ));
    }

    articleCodes.value = [
        ...selectedCleaningProducts.map(a => a.code),
        ...selectedAccessories.map(a => a.code),
    ];

    // Load article photos
    fetchImages();
}

async function fetchImages()
{
    try
    {
        await store.dispatch('catalog/fetchImagesByArticleCodes', {
            eid:    store.state.account.cEntity.id,
            codes:  articleCodes.value,
            format: 'T',
        });
    }
    catch(error)
    {
        // TODO: Show error
    }
}


// ------------------------------------------------------------ LIFECYCLE HOOKS

onMounted(() =>
{
    suggest();
});
</script>


<!-- /////////////////////////////////////////////////////////////////////////// STYLE -->

<style scoped>
@reference "#main.css";

.sl-related-articles
{
    @apply grid grid-cols-4 text-gray-400;
}

.sl-related-articles__item
{
    @apply border-0 border-l;
}

.sl-related-articles__item:first-child
{
    @apply border-l-0;
}
</style>
